import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, Icon } from '@hydrodigital/design-system';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import ClockSvg from '@/assets/svg/ClockSvg';
import type { AlertCardType, StrategyOption, ObjectiveFunction } from '@/domain/Notifications/Notifications';
import { CARD_CONFIG, STRATEGY_STALE_MINUTES } from '../constants';
import Chip from '../components/Chip';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

interface StrategyCardProps {
	readonly act: AlertCardType & { onDismiss?: () => void };
}

export default function StrategyCard({ act }: StrategyCardProps) {
	useLocale();
	const [expanded, setExpanded] = useState(false);

	const options: StrategyOption[] = act.strategies ?? [];

	// Default to the recommended strategy, or the first one
	const defaultStrategy = useMemo(() => {
		const rec = options.find((o) => o.isRecommended);
		return rec?.name ?? options[0]?.name ?? '';
	}, [options]);

	const [selectedStrategy, setSelectedStrategy] = useState<string>(defaultStrategy);

	const activeOption = useMemo(
		() => options.find((o) => o.name === selectedStrategy) ?? options[0],
		[selectedStrategy, options],
	);

	const isRecommended = activeOption?.isRecommended ?? true;
	const cardType = isRecommended ? 'strategy' : 'strategy-not-recommended';
	const cardConfig = CARD_CONFIG[cardType];
	const accentColor = isRecommended ? '#34C38F' : '#E8453C';

	const activeOF: ObjectiveFunction | undefined = activeOption?.objectiveFunction;

	// AC#04 — red timestamp if strategy is older than STRATEGY_STALE_MINUTES
	const secInfo = localStorage.getItem('security');
	const tzOffset = Number(JSON.parse(secInfo || 'null')?.timeZone ?? 0);
	const dt = dayjs(act.datetime, 'YYYY/MM/DD hh:mm:ss A').utcOffset(tzOffset * 60, true);
	const userNow = dayjs().utcOffset(tzOffset * 60);
	const dateLabel = dt.isValid() ? `${dt.format('YYYY-MM-DD hh:mma')} (${dt.from(userNow)})` : '';
	const isStale = dt.isValid() && userNow.diff(dt, 'minute') > STRATEGY_STALE_MINUTES;

	return (
		<div
			style={{
				border: `1.5px solid ${cardConfig.borderColor}`,
				background: cardConfig.cardBg,
				borderRadius: 12,
				padding: '14px 16px',
				marginBottom: 14,
				position: 'relative',
				width: '100%',
			}}
		>
			{/* Dismiss button */}
			{act.onDismiss && act.dispatchId && (
				<CloseCircleOutlined
					onClick={(e) => {
						e.stopPropagation();
						act.onDismiss?.();
					}}
					style={{
						position: 'absolute',
						top: 13,
						right: 14,
						fontSize: 16,
						cursor: 'pointer',
						color: '#8C8C8C',
					}}
					aria-label="Dismiss notification"
					title="Dismiss"
				/>
			)}

			{/* Title row */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					fontWeight: 700,
					fontSize: 14,
					color: isRecommended ? '#1A1A2E' : '#E8453C',
					paddingRight: 28,
				}}
			>
				{cardConfig.icon}
				<span>{isRecommended ? 'RECOMMENDED STRATEGY' : 'STRATEGY NOT RECOMMENDED'}</span>
			</div>

			{/* Message */}
			{act.message && (
				<div style={{ fontSize: 13, fontWeight: 500, color: '#333', margin: '8px 0 6px 0', lineHeight: 1.5 }}>
					{act.message}
				</div>
			)}

			{/* Strategy combobox */}
			{options.length > 0 && (
				<div style={{ margin: '8px 0' }}>
					<Combobox
						items={options.map((opt) => ({
							value: opt.name,
							label: opt.name,
						}))}
						value={selectedStrategy}
						onValueChange={(val) => setSelectedStrategy(val)}
					>
						<div className="[&_input]:font-bold [&_input]:text-base">
							<ComboboxInput placeholder="Select strategy" />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No strategies found.</ComboboxEmpty>
							<ComboboxList>
								{(item) => (
									<ComboboxItem key={item.value} value={item}>
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
			)}

			{/* Datetime — red font if stale (AC#04) */}
			{dateLabel && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						margin: '6px 0',
						color: isStale ? '#E8453C' : '#555',
						fontSize: 13,
						fontWeight: 600,
					}}
				>
					<Icon children={<ClockSvg />} style={{ fontSize: 12 }} />
					<span>{dateLabel}</span>
				</div>
			)}

			{/* Expanded details */}
			{expanded && (
				<>
					{activeOF && (
						<div
							style={{
								fontSize: 13,
								fontWeight: 600,
								borderBottom: `1px solid ${accentColor}`,
								paddingBottom: 4,
								display: 'flex',
								gap: '2px',
								alignItems: 'center',
								color: '#000',
							}}
						>
							<Chip icon={null}>{m.optimization_target()}</Chip>
							<span>
								{activeOF.name ?? 'N/A'}: {activeOF.value != null ? activeOF.value.toFixed(3) : 'No data'}
							</span>
						</div>
					)}

					<div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, color: '#000' }}>
						{activeOF?.kpis?.map((kpi, i) => (
							<div
								key={i}
								style={{
									fontSize: 13,
									fontWeight: 600,
									borderBottom: `1px solid ${accentColor}`,
									paddingBottom: 4,
								}}
							>
								<Icon name="chart-line" color="#000" />
								<span className="font-bold">
									{' '}
									[Safety Priority {i + 1}] {kpi.name}:{' '}
									{kpi.value !== null && kpi.value !== undefined
										? kpi.value === 0
											? '0'
											: kpi.value.toFixed(3)
										: 'No data'}
								</span>
							</div>
						))}
					</div>

					<div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6, color: '#000' }}>
						{act.locationName && (
							<div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
								<Icon name="map-marker" color="#000" />
								<span style={{ fontWeight: 600 }}>{act.locationName}</span>
							</div>
						)}
						{(act.modelId || act.modelName) && (
							<div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#000' }}>
								<Icon name="virtual-twin" color="#000" />
								<span style={{ fontWeight: 600 }}>
									{act.modelId} - {act.modelName}
								</span>
							</div>
						)}
					</div>
				</>
			)}

			{/* View more / View less toggle */}
			<Button
				onClick={() => setExpanded((v) => !v)}
				iconName={expanded ? 'angle-up' : 'angle-down'}
				showIcon
				showLabel
				size="minimal"
				variant="primary"
				style={{
					display: 'flex',
					alignItems: 'center',
					marginTop: 10,
					cursor: 'pointer',
					userSelect: 'none',
				}}
				label={expanded ? 'View less' : 'View more'}
			/>
		</div>
	);
}
